import { chartHasValues } from "@agent/chartData";
import type { UiChartNode } from "@agent/uiSchema";
import { flushSync } from "react-dom";
import { createRoot, type Root } from "react-dom/client";

export const chartNodes = new WeakMap<Element, UiChartNode>();

/** HTML capture clones cannot run the original chart's ResizeObserver. */
export const prepareChartExports = async (
  source: HTMLElement,
  clone: HTMLElement,
) => {
  const originals = source.querySelectorAll("[data-ui-chart]");
  if (!originals.length) return;
  const { ChartGraphic } = await import("@agent/ChartGraphic");
  const copies = clone.querySelectorAll("[data-ui-chart]");
  const roots: Root[] = [];
  try {
    originals.forEach((original, index) => {
      const node = chartNodes.get(original);
      const target =
        copies[index]?.querySelector<HTMLElement>("[data-chart-plot]");
      if (!node || !target || !chartHasValues(node)) return;
      const width = target.getBoundingClientRect().width;
      if (width <= 0) return;
      // One chart failing keeps its on-screen markup; the rest of the PDF still exports.
      const previous = Array.from(target.childNodes);
      target.replaceChildren();
      const root = createRoot(target);
      try {
        flushSync(() =>
          root.render(
            <ChartGraphic node={node} width={width} interactive={false} />,
          ),
        );
        roots.push(root);
      } catch (err) {
        console.error("Chart PDF export failed:", err);
        root.unmount();
        target.replaceChildren(...previous);
      }
    });
    // Recharts registers axes and series in layout effects before its final paint.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    const rendered = copies.values();
    for (const copy of rendered) {
      const plot = copy.querySelector("[data-chart-plot]");
      if (!plot) continue;
      // SVG images cannot inherit CSS variables after html2canvas serializes them.
      for (const element of plot.querySelectorAll<SVGElement>("svg, svg *")) {
        const style =
          clone.ownerDocument.defaultView!.getComputedStyle(element);
        for (const property of [
          "fill",
          "stroke",
          "color",
          "font-family",
          "font-size",
          "font-weight",
        ]) {
          if (element.getAttribute(property)?.startsWith("url(")) continue;
          element.style.setProperty(property, style.getPropertyValue(property));
        }
      }
      const snapshot = plot.cloneNode(true);
      plot.replaceWith(snapshot);
    }
  } finally {
    roots.forEach((root) => root.unmount());
  }
};
